/* eslint-disable eslint-comments/no-restricted-disable */
import type { EntryPoint, EntryPointComponent } from 'react-relay';
import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';

import { JSResourceForUserVisible } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import type { Match } from '@atlassian/react-resource-router';
import { JiraSiteAri } from '@atlassian/ari/jira/site';
import { fg } from '@atlassian/jira-feature-gating';
import parameters from './__generated__/WorkItemEntrypointUiQuery$parameters.ts';
import type {
	JiraResourceIntegration,
	WorkItemEntrypointUiQuery,
} from './__generated__/WorkItemEntrypointUiQuery.graphql.ts';

const CHILD_ISSUES_INITIAL_DISPLAY_COUNT = 5;

/** Forge module types for work item query. Keep in sync with workItemForgeTypes in @jira/work-item. */
const WORK_ITEM_FORGE_TYPES = [
	'jira:issueAction',
	'jira:issueGlance',
	'jira:issueContext',
	'jira:issuePanel',
	'jira:issueActivity',
	'jira:customField',
	'jira:customFieldType',
	'jira:issueViewBackgroundScript',
	'jira:uiModifications',
] as const;

const getIssueKey = (match: Match) => {
	return match?.params?.issueKey?.length ? match.params.issueKey : null;
};

const extractProjectKey = (issueKey: string): string => issueKey.split('-')[0];

const RESOURCES_INTEGRATION: Record<string, JiraResourceIntegration> = {
	ATTACHMENT: 'ATTACHMENT',
	LOOM: 'LOOM',
	CONFLUENCE: 'CONFLUENCE',
	WHITEBOARD: 'WHITEBOARD',
	GDRIVE: 'GDRIVE',
	WEBLINK: 'WEBLINK',
};

const getIssueViewAddAttachmentsFlag = (tenantContext: EntryPointRouteParams['tenantContext']) =>
	tenantContext?.appEditions?.software === 'FREE_EDITION' &&
	tenantContext?.licenseStates?.software === 'ACTIVE';

const getDefaultAttachmentFilters = () => ({
	defaultAttachmentFilters: [
		{ integration: RESOURCES_INTEGRATION.CONFLUENCE },
		{ integration: RESOURCES_INTEGRATION.WHITEBOARD },
		{ integration: RESOURCES_INTEGRATION.LOOM },
		{ integration: RESOURCES_INTEGRATION.ATTACHMENT, types: [] },
		{ integration: RESOURCES_INTEGRATION.WEBLINK },
	],
});
type WorkItemEntrypointComponent = EntryPointComponent<
	{
		workItemQuery: WorkItemEntrypointUiQuery;
	},
	{},
	{},
	{}
>;

export const workItemEntrypoint: EntryPoint<WorkItemEntrypointComponent, EntryPointRouteParams> =
	createEntryPoint({
		root: JSResourceForUserVisible<WorkItemEntrypointComponent>(
			/* webpackChunkName: "async-work-item-view-entrypoint" */ './WorkItemSanitisedEntrypoint.tsx',
			{
				entryFsSsrLiftImportToModule: true,
			},
		),
		getPreloadProps: ({
			context,
			tenantContext,
			tenantContext: { cloudId, isAnonymous },
		}: EntryPointRouteParams) => {
			const { match } = context;
			const issueKey = getIssueKey(match);
			const projectKey = issueKey ? extractProjectKey(issueKey) : '';
			const jql = projectKey ? `project = "${projectKey}"` : '';
			const focusedCommentId = context.query?.focusedCommentId
				? String(context.query.focusedCommentId)
				: null;
			// Logic from getChildIssuesFieldIdsQueryVariables (main-issue-agg-query). fg() only in `if`
			// conditions — not inside `const queryVariables = { fg() }` (jira/ff/inline-usage).
			const childIssuesFieldIds = ['issuetype'];
			if (!fg('relay-migration-edit-worklog') && !fg('jpx-93-unship-legacy-timetracking')) {
				childIssuesFieldIds.push('timetracking');
			}
			if (!fg('jira-issue-view-child-issues-panel-realtime-update')) {
				childIssuesFieldIds.push('status');
			}
			// Design panel + child-issue vars + master preload fields (issueTypeId / favourite fields).
			return {
				queries: {
					workItemQuery: {
						options: {
							fetchPolicy: 'store-and-network' as const,
						},
						variables: {
							cloudId: cloudId || '',
							issueKey: issueKey || '',
							projectKey,
							jql,
							siteAri: cloudId ? JiraSiteAri.create({ siteId: cloudId }).toString() : '',
							// wi2-design-panel-data: design panel data is now fetched via
							// connectedToIssueAggDataResource (ConnectedToIssueAggQuery) and passed
							// into WorkItemLayout as connectedAggData. When this gate is on, we set
							// these to false/false to remove the fields from the main blocking query.
							consistentRead: fg('wi2-design-panel-data')
								? false
								: fg('design-panel-ags-consistent-read'),
							includeDesignPanelData: fg('wi2-design-panel-data') ? false : Boolean(!isAnonymous),
							includeFigmaConfigData: fg('remove_ctiaq_config_state')
								? Boolean(!isAnonymous)
								: false,
							commentTargetId: focusedCommentId,
							hasCommentTargetId: !!focusedCommentId,
							applicationKey: 'JIRA_SOFTWARE' as const,
							issueViewAddAttachmentFlag: !fg('wi2_1p_attachments_resource')
								? getIssueViewAddAttachmentsFlag(tenantContext)
								: false,
							forgeTypes: [...WORK_ITEM_FORGE_TYPES],
							issueSearchInput: fg('jira-issue-view-child-work-items-ssr-fg')
								? { jql: issueKey ? `parent = ${issueKey}` : '' }
								: { childIssuesInput: { parentIssueKey: issueKey || '' } },
							fieldSetsInput: {
								viewInput: {
									namespace: 'CHILD_ISSUE_PANEL',
									context: { issueContext: { issueKey } },
								},
							},
							childIssuesFieldIds,
							childIssuesAmountOfColumns: 500,
							childIssuesFirst: fg('jira_child_work_items_pagination')
								? CHILD_ISSUES_INITIAL_DISPLAY_COUNT
								: undefined,
							childWorkItemsSSRUIEnabled: Boolean(fg('jira-issue-view-child-work-items-ssr-fg')),
							shouldUseNewChildWorkItemsDatafetcher: true,
							isInitialLoadQuery: true,
							shouldCaptureEntrypointQuery5xx: Boolean(
								fg('wi2-work-item-entrypoint-query-5xx-capture'),
							),
							includeAgenticWorkItemData: Boolean(fg('enable_work_item_for_quadratic_release')),
							sideBarLayoutDecompositionEnabled: Boolean(fg('side-layout-work-item-integration')),
							...getDefaultAttachmentFilters(),
						},
						parameters,
					},
				},
			};
		},
	});
